import React, { type FC } from 'react';
import { FormattedMessage, injectIntl, type WrappedComponentProps } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import Badge from '@atlaskit/badge';
import SearchIcon from '@atlaskit/icon/core/search';
import AddIcon from '@atlaskit/icon/core/add';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';

import { ContentStateColor } from '../../ContentStateColor.tsx';
import type { ContentState } from '../types.ts';

import { i18n } from './contentStateMenuV2I18n.ts';

export const IN_REVIEW_VALUE = 'in-review';

const styles = cssMap({
	dialog: {
		minWidth: '320px',
		maxWidth: '360px',
	},
	row: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		justifyContent: 'space-between',
	},
	searchInput: {
		paddingInline: token('space.150'),
		paddingTop: token('space.100'),
	},
	after: {
		paddingInlineEnd: token('space.075'),
	},
	inReviewStartButton: {
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		color: token('color.text.subtlest'),
		gap: token('space.100'),
		alignItems: 'center',
	},
	inReviewIcon: {
		color: token('color.text.accent.blue'),
		paddingRight: token('space.050'),
	},
	changeStatusHeader: {
		paddingInline: token('space.150'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.100'),
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	changeStatusHeaderLeft: {
		alignItems: 'center',
		gap: token('space.050'),
	},
});

const getIconBefore = ({ option }: { option: ContentState }) => {
	const isApprovalOption = option.id === IN_REVIEW_VALUE;

	if (isApprovalOption) {
		return (
			<Box xcss={styles.inReviewIcon} testId="in-review-status-icon">
				<CheckCircleIcon label="" size="small" />
			</Box>
		);
	}

	return (
		<ContentStateColor
			color={option.color}
			restrictionLevel={option.restrictionLevel}
			appearance="contentStateButton"
		/>
	);
};

type StatusOptionRowProps = {
	option: ContentState;
	onSelect: (status: ContentState) => void;
};

const StatusOptionRow: FC<StatusOptionRowProps> = ({ option, onSelect }) => {
	const handleClick = () => {
		if (option && option.name) {
			onSelect(option);
		}
	};

	return (
		<ButtonItem iconBefore={getIconBefore({ option })} onClick={handleClick}>
			<Inline xcss={styles.row}>
				{option.name}
				{option.id === IN_REVIEW_VALUE ? (
					<Inline xcss={styles.inReviewStartButton}>
						<Badge appearance="neutral">
							<FormattedMessage {...i18n.startApproval} />
						</Badge>
						<ChevronRightIcon size="small" label="" />
					</Inline>
				) : null}
			</Inline>
		</ButtonItem>
	);
};

type ContentStateMenuV2SelectViewProps = WrappedComponentProps & {
	searchValue: string;
	onSearchChange: (value: string) => void;
	filteredDefaultOptions: ContentState[];
	filteredCustomOptions: ContentState[];
	hasResults: boolean;
	hasCurrentStatus: boolean;
	onSelectStatus: (status: ContentState) => void;
	onAddCustomClick: () => void;
	onRemove: () => void;
	onBack: () => void;
};

const ContentStateMenuV2SelectViewComponent: FC<ContentStateMenuV2SelectViewProps> = ({
	searchValue,
	onSearchChange,
	filteredDefaultOptions,
	filteredCustomOptions,
	hasResults,
	hasCurrentStatus,
	onSelectStatus,
	onAddCustomClick,
	onRemove,
	onBack,
	intl: { formatMessage },
}) => (
	<Stack xcss={styles.dialog} testId="status-selector-popup-menu">
		{hasCurrentStatus && (
			<Inline xcss={styles.changeStatusHeader} testId="change-status-header">
				<Inline xcss={styles.changeStatusHeaderLeft} space="space.100">
					<IconButton
						icon={ArrowLeftIcon}
						label="Back"
						spacing="compact"
						onClick={onBack}
						testId="change-status-back-button"
					/>
					<Heading as="h4" size="xsmall">
						{formatMessage(i18n.changeStatus)}
					</Heading>
				</Inline>
				<Button onClick={onRemove} spacing="compact" testId="clear-status-button">
					{formatMessage(i18n.clear)}
				</Button>
			</Inline>
		)}
		<MenuGroup maxHeight={360}>
			<Section>
				<Inline xcss={styles.searchInput}>
					<Textfield
						name="status-search"
						aria-label={formatMessage(i18n.searchPlaceholder)}
						placeholder={formatMessage(i18n.searchPlaceholder)}
						maxLength={20}
						isCompact
						value={searchValue}
						onChange={(e) => onSearchChange(e.currentTarget.value)}
						elemAfterInput={
							<Box xcss={styles.after}>
								<SearchIcon label="" />
							</Box>
						}
						testId="status-search-input"
					/>
				</Inline>
			</Section>
			<Section isScrollable>
				{filteredDefaultOptions.length > 0 && (
					<Section title={formatMessage(i18n.defaultWorkflow)}>
						<MenuGroup>
							{filteredDefaultOptions.map((option) => (
								<StatusOptionRow key={option.id} option={option} onSelect={onSelectStatus} />
							))}
						</MenuGroup>
					</Section>
				)}
				{filteredCustomOptions.length > 0 && (
					<Section title={formatMessage(i18n.customStatuses)}>
						{filteredCustomOptions.map((option) => (
							<StatusOptionRow key={option.id} option={option} onSelect={onSelectStatus} />
						))}
					</Section>
				)}
				{!hasResults && searchValue.trim() && (
					<Section>
						<ButtonItem
							iconBefore={<AddIcon label="" />}
							description={formatMessage(i18n.addCustomStatusDescription)}
							onClick={onAddCustomClick}
							testId="add-custom-status-button"
						>
							{formatMessage(i18n.addCustomStatus)}
						</ButtonItem>
					</Section>
				)}
			</Section>
		</MenuGroup>
	</Stack>
);

export const ContentStateMenuV2SelectView = injectIntl(ContentStateMenuV2SelectViewComponent);
